'use client';

import { useEffect, useRef, useState } from 'react';
import { deserialize, serialize } from '../../../../utils/serialize';

export interface UseStorageOptions {
  /**
   * Writes the fallback value to local/session storage if there isn't a value
   * set.
   */
  writeFallbackValueIfUsed?: boolean;
  /**
   * Overrides the value set by StorageKeyPrefixProvider.
   */
  customPrefix?: string;
}

export default function useStorage_InternalUseOnly<T>(
  type: 'local' | 'session',
  key: string,
  fallbackValue: T,
  { writeFallbackValueIfUsed }: UseStorageOptions = {},
) {
  const isMountedRef = useRef(false);
  const [value, setValue] = useState(fallbackValue);

  const serializedFallbackValue = serialize(fallbackValue);

  useEffect(() => {
    const storage = window[`${type}Storage`];
    const serializedStoredItem = storage.getItem(key);

    if (
      serializedStoredItem !== null &&
      serializedStoredItem !== serializedFallbackValue
    ) {
      setValue(deserialize(serializedStoredItem));
    }

    if (writeFallbackValueIfUsed && serializedStoredItem === null) {
      // Write fallback value through to local/session storage if it's used
      storage.setItem(key, serializedFallbackValue);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [type, key, serializedFallbackValue, writeFallbackValueIfUsed]);

  useEffect(() => {
    if (isMountedRef.current) {
      const storage = window[`${type}Storage`];
      const serializedValue = serialize(value);

      if (storage.getItem(key) !== serializedValue) {
        storage.setItem(key, serializedValue);
      }
    } else {
      isMountedRef.current = true;
    }
  }, [type, key, value]);

  return [value, setValue] as const;
}
